import { useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../../hooks/useNotifications'
import { getMergeChainEndPersonId, getPersonCard, getPersonLabels } from '../../../../services/personService'
import { getUser } from '../../../../services/userService'
import { isNotNullOrUndefinedOrEmpty } from '../../../../util/Utils'

export function useQueueCaches() {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const USERS_CACHE_LIMIT = 1000

  const initialState = {
    personsCache: {},
    labelsCache: {},
    usersCache: {},
    usersCacheOrder: [], // List of userIds in cache order
  }

  const reducer = (_, currentState) => {
    return { ...currentState }
  }

  const [queueCachesState, setQueueCachesState] = useReducer(reducer, initialState)

  const updateUsersCache = (userId) => {
    // Move userId to front if already present, else push to front
    const idx = queueCachesState.usersCacheOrder.indexOf(userId)
    if (idx !== -1) {
      queueCachesState.usersCacheOrder.splice(idx, 1)
      queueCachesState.usersCacheOrder.unshift(userId)
    } else {
      queueCachesState.usersCacheOrder.unshift(userId)
    }
    // Cap usersCacheOrder at USERS_CACHE_LIMIT
    while (queueCachesState.usersCacheOrder.length > USERS_CACHE_LIMIT) {
      const removedId = queueCachesState.usersCacheOrder.pop()
      delete queueCachesState.usersCache[removedId]
    }
  }

  const queueCachesActions = {
    appendFact: (personCard, factType) => {
      let foundFact = {}
      personCard?.facts?.forEach((fact) => {
        if (fact?.value?.type?.toLowerCase()?.includes(factType)) {
          foundFact = fact
        }
      })
      if (isNotNullOrUndefinedOrEmpty(foundFact)) {
        personCard[factType] = foundFact
      }
    },
    appendMarriages: (personCard) => {
      const marriages = []
      personCard?.pairings?.forEach((pairing) => {
        if (pairing?.coupleFactValue?.type?.toLowerCase()?.includes('marriage')) {
          marriages.push({
            value: {
              date: pairing.coupleFactValue.date,
              place: pairing.coupleFactValue.place,
              conclusionId: pairing.coupleFactValue.conclusionId,
            },
          })
        }
      })
      if (marriages.length > 0) {
        personCard.marriages = marriages
      }
    },
    fetchPersonSummary: (personId) => {
      if (!queueCachesState.personsCache[personId]) {
        queueCachesState.personsCache[personId] = {}
        getPersonCard(personId)
          .then((personCard) => {
            queueCachesActions.appendFact(personCard, 'birth')
            queueCachesActions.appendFact(personCard, 'death')
            queueCachesActions.appendFact(personCard, 'burial')
            queueCachesActions.appendFact(personCard, 'christening')
            queueCachesActions.appendMarriages(personCard)
            queueCachesState.personsCache[personId] = personCard
            queueCachesActions.updateQueueCachesState()
          })
          .catch((err) => {
            error(
              t('queues.errors.failedToGetPersonData', {
                defaultValue: `Failed to get Person Data for Person ID: ${personId}`,
                personId,
              }),
              err
            )
          })
      }
    },
    fetchPersonLabels: async (personId) => {
      if (!queueCachesState.labelsCache[personId]) {
        queueCachesState.labelsCache[personId] = []
        const mergeChainEndPersonId = await getMergeChainEndPersonId(personId)
          .then((response) => response.data.personId)
          .catch(() => personId)
        getPersonLabels(mergeChainEndPersonId)
          .then((data) => {
            queueCachesState.labelsCache[personId] = data.labels
            queueCachesActions.updateQueueCachesState()
          })
          .catch((err) => {
            error(
              t('queues.errors.failedToGetLabels', {
                defaultValue: `Failed to get labels for Person ID: ${personId}`,
                personId,
              }),
              err
            )
          })
      }
    },
    fetchUserSummary: (userId) => {
      updateUsersCache(userId)
      if (!queueCachesState.usersCache[userId] && queueCachesState.usersCacheOrder.includes(userId)) {
        queueCachesState.usersCache[userId] = {}
        getUser(userId, userId.startsWith('cis.user.') ? 'cisId' : 'contributorId')
          .then((response) => {
            queueCachesState.usersCache[userId] = {
              contactName: response.data?.identity?.users[0]?.contactName,
              country: response.data?.identity?.users[0]?.country,
            }
            queueCachesActions.updateQueueCachesState()
          })
          .catch((err) => {
            error(
              t('queues.errors.failedToGetUserData', {
                defaultValue: `Failed to get User Data and Cis Data for User ID: ${userId}`,
                userId,
              }),
              err
            )
          })
      }
      queueCachesActions.updateQueueCachesState()
    },

    fetchUserSummaries: async (userIds) => {
      Array.from(userIds).forEach((userId) => updateUsersCache(userId))
      await Promise.allSettled(
        Array.from(userIds)
          .filter((userId) => !queueCachesState.usersCache[userId] && queueCachesState.usersCacheOrder.includes(userId)) // Only fetch users not already in the cache
          .map((userId) =>
            getUser(userId, userId.startsWith('cis.user.') ? 'cisId' : 'contributorId')
              .then((response) => {
                queueCachesState.usersCache[userId] = {
                  contactName: response.data?.identity?.users[0]?.contactName,
                  country: response.data?.identity?.users[0]?.country,
                }
              })
              .catch((err) => {
                error(
                  t('queues.errors.failedToGetUserData', {
                    defaultValue: `Failed to get User Data and Cis Data for User ID: ${userId}`,
                    userId,
                  }),
                  err
                )
              })
          )
      )
      queueCachesActions.updateQueueCachesState()
    },
    updateQueueCachesState: () => {
      setQueueCachesState(queueCachesState)
    },
  }

  return { queueCachesState, queueCachesActions }
}
